/* The line model of a care-plan questionnaire answer, shared by the browser and
 * the server.
 *
 * A care-plan answer is a scaffold: literal `Label: <br>` runs, sometimes
 * grouped under `<h4>` sub-headings that let the same label repeat. Two ends
 * need to agree about it exactly. The browser PLACES a suggested value onto one
 * of those lines, addressing it by index; the AI read tool ENUMERATES the lines
 * so the model picks a label that exists instead of inferring one from raw HTML.
 * When the two disagree the model is offered a label the client cannot place,
 * which is the whole class of failure this module exists to remove.
 *
 * So the rule lives here once. What cannot live here is the parse: the browser
 * has `DOMParser` and the server has `node-html-parser`, so each supplies an
 * `AnswerHtmlParse` adapter over the small node shape below, and every rule
 * downstream of it — what counts as a line, what counts as a heading, which
 * lines a section owns, when a label matches — is this module's.
 *
 * The browser's WRITE path still walks its own live DOM, because a rewrite
 * mutates nodes this shape does not carry. It splits lines by the same exported
 * constants and asks the same exported predicates, so the traversal is the only
 * thing that is written twice.
 */

/* One top-level node of a parsed answer, as both parsers can describe it. */
export type AnswerHtmlNode =
  | { kind: 'text'; text: string }
  | {
      kind: 'element'
      /* Case-insensitive; normalized here, so adapters need not agree. */
      tagName: string
      innerHtml: string
      outerHtml: string
      text: string
    }

/* Parses an answer fragment into its top-level nodes. */
export type AnswerHtmlParse = (html: string) => AnswerHtmlNode[]

/* Blocks whose inner `<br>`-separated runs are separately meaningful. Anything
 * else (a list, a table) stays whole, since splitting it would destroy its
 * structure. */
export const LINE_BEARING_TAGS = new Set(['P', 'DIV', 'H1', 'H2', 'H3', 'H4'])

export const HEADING_TAGS = new Set(['H1', 'H2', 'H3', 'H4'])

export const ANSWER_LINE_BREAK = /<br\s*\/?>/i

const TRAILING_COLON = /:$/

export function isLineBearingTag(tagName: string): boolean {
  return LINE_BEARING_TAGS.has(tagName.toUpperCase())
}

export function isHeadingTag(tagName: string): boolean {
  return HEADING_TAGS.has(tagName.toUpperCase())
}

/* The rendered text of a fragment, with no markup. */
export function answerLineText(html: string, parse: AnswerHtmlParse): string {
  return parse(html)
    .map((node) => node.text)
    .join('')
}

/* A line belongs to a label when its text opens with that label. The trailing
 * colon is what stops `Status:` from claiming a `Status Update:` line, and
 * matching a PREFIX rather than the whole line is what lets a label that
 * already carries a value still be found. */
export function matchesLabel(
  lineHtml: string,
  label: string,
  parse: AnswerHtmlParse
): boolean {
  const target = label.trim().toLowerCase()
  return (
    target !== '' &&
    answerLineText(lineHtml, parse).trim().toLowerCase().startsWith(target)
  )
}

/* Headings compare whole, never by prefix, so `PT` cannot claim `PT/OT`. A
 * trailing colon is dropped on both sides because the caller copies whichever
 * spelling it saw and a heading may carry one. */
export function normalizeHeading(text: string): string {
  return text.trim().replace(TRAILING_COLON, '').trim().toLowerCase()
}

/* The answer's lines, in the order and count every index addresses. */
export function toAnswerLines(html: string, parse: AnswerHtmlParse): string[] {
  if (!html.trim()) {
    return []
  }
  const lines: string[] = []
  for (const node of parse(html)) {
    if (node.kind === 'text') {
      if (node.text.trim()) {
        lines.push(node.text)
      }
      continue
    }
    if (!isLineBearingTag(node.tagName)) {
      lines.push(node.outerHtml)
      continue
    }
    for (const part of node.innerHtml.split(ANSWER_LINE_BREAK)) {
      lines.push(part)
    }
  }
  // A trailing empty line is an artifact of a block ending in <br>, not content.
  while (lines.length > 0 && (lines.at(-1) ?? '').trim() === '') {
    lines.pop()
  }
  return lines
}

export interface AnswerHeading {
  index: number
  text: string
}

/* A section heading is a line of an `<h1>`–`<h4>` carrying text, and nothing
 * else. Boldness is not a heading: the scaffolds bold their LABELS, so an
 * unfilled `<strong>Goal:</strong>` is a wholly bold line, and reading that as a
 * heading truncates the section it sits in and hides it from the label search —
 * in exactly the blank state a proposal targets. */
export function toAnswerHeadings(
  html: string,
  parse: AnswerHtmlParse
): AnswerHeading[] {
  const headings: AnswerHeading[] = []
  let index = 0
  for (const node of parse(html)) {
    if (node.kind === 'text') {
      if (node.text.trim()) {
        index += 1
      }
      continue
    }
    if (!isLineBearingTag(node.tagName)) {
      index += 1
      continue
    }
    const lines = node.innerHtml.split(ANSWER_LINE_BREAK)
    if (isHeadingTag(node.tagName)) {
      for (const [offset, line] of lines.entries()) {
        if (answerLineText(line, parse).trim() !== '') {
          headings.push({
            index: index + offset,
            text: answerLineText(line, parse).trim(),
          })
        }
      }
    }
    index += lines.length
  }
  return headings
}

export interface LineSpan {
  start: number
  end: number
}

/* Which lines a lookup may read: the whole answer when no section was asked
 * for, the range a heading owns when it was, and nothing at all when the
 * section asked for is not in the answer.
 *
 * That last case must not fall back to the whole answer. A section is how a
 * repeated label is told apart, so searching the whole answer for a section the
 * answer does not hold lands on the FIRST line carrying that label — some other
 * section's. The value belongs to a section this document does not have, so it
 * is not placed. */
export type SectionScope =
  | { kind: 'answer' }
  | { kind: 'section'; span: LineSpan }
  | { kind: 'missing' }

export function sectionScope(
  headings: readonly AnswerHeading[],
  lineCount: number,
  section: string | undefined
): SectionScope {
  const target = normalizeHeading(section ?? '')
  if (target === '') {
    return { kind: 'answer' }
  }
  let start: number | null = null
  for (const heading of headings) {
    if (start !== null) {
      return { kind: 'section', span: { start, end: heading.index } }
    }
    if (normalizeHeading(heading.text) === target) {
      start = heading.index + 1
    }
  }
  return start === null
    ? { kind: 'missing' }
    : { kind: 'section', span: { start, end: lineCount } }
}

/* Locates the scaffold line whose label matches, ignoring case and surrounding
 * whitespace, within `section` when one is given. The index is into
 * `toAnswerLines`. Null when the answer has no such line. */
export function findLabelLine(
  html: string,
  label: string,
  section: string | undefined,
  parse: AnswerHtmlParse
): number | null {
  const lines = toAnswerLines(html, parse)
  const headings = toAnswerHeadings(html, parse)
  const scope = sectionScope(headings, lines.length, section)
  if (scope.kind === 'missing') {
    return null
  }
  const headingIndices = new Set(headings.map((heading) => heading.index))
  const start = scope.kind === 'section' ? scope.span.start : 0
  const end =
    scope.kind === 'section'
      ? Math.min(scope.span.end, lines.length)
      : lines.length
  for (let index = start; index < end; index += 1) {
    if (
      !headingIndices.has(index) &&
      matchesLabel(lines[index] ?? '', label, parse)
    ) {
      return index
    }
  }
  return null
}

/* One scaffold line a suggestion may be written onto.
 *
 * `section` is present only where it is NEEDED — where the same label occurs
 * under more than one heading — because that is exactly the rule the model is
 * asked to follow, and a section on an unambiguous label would search a scope
 * the label does not live in. */
export interface AnswerScaffoldLabel {
  label: string
  section?: string
}

/* Every label line of an answer, as the options a proposal may target.
 *
 * This is the point of the module. The model used to be handed the answer as
 * raw HTML and told, in prose, how to find the label lines in it and how to copy
 * them — so the identifiers it sent were inferred, and an inference that missed
 * produced a value with nowhere to go. Enumerated here, from the same rules the
 * client places by, the participant's own answer becomes the literal list of
 * options.
 *
 * A label is the text up to and including its colon. A line with no colon is not
 * a label line: it is prose, or a scaffold hint like `If none, document "Not
 * Indicated."`, and offering it would invite a write onto a line with no field.
 */
export function enumerateAnswerLabels(
  html: string,
  parse: AnswerHtmlParse
): AnswerScaffoldLabel[] {
  const lines = toAnswerLines(html, parse)
  const headings = toAnswerHeadings(html, parse)
  const headingIndices = new Set(headings.map((heading) => heading.index))
  const found: { label: string; section: string | undefined }[] = []
  for (const [index, line] of lines.entries()) {
    if (headingIndices.has(index)) {
      continue
    }
    const text = answerLineText(line, parse).trim()
    const colon = text.indexOf(':')
    if (colon < 0) {
      continue
    }
    const label = text.slice(0, colon + 1).trim()
    if (label === ':') {
      continue
    }
    // The heading this line sits under: the last one declared above it.
    const section = headings.filter((h) => h.index < index).at(-1)?.text
    found.push({ label, section })
  }
  /* A section qualifier is reported only for a label the answer repeats. An
   * unrepeated label takes none — sending one would scope the search to a
   * heading the client then has to find, and the first occurrence of a label
   * that sits ABOVE every heading has no section to name at all. */
  const counts = new Map<string, number>()
  for (const entry of found) {
    const key = entry.label.toLowerCase()
    counts.set(key, (counts.get(key) ?? 0) + 1)
  }
  return found.map(({ label, section }) =>
    (counts.get(label.toLowerCase()) ?? 0) > 1 && section !== undefined
      ? { label, section }
      : { label }
  )
}
